<template>
  <div :class="$style.select">
    <button
      type="button"
      :class="[{[$style.active]: isExpanded },
               $style.selectedOption]"
      aria-haspopup="listbox"
      :aria-expanded="isExpanded"
      @click="toggleSelectHandler"
    >
      <span>
        {{ selectedOption }}
      </span>
      <ion-icon
        :class="$style.angleDownIcon"
        name="ios-arrow-down"
      />
    </button>
    <transition name="select-options">
      <ul
        v-show="isExpanded"
        id="region-list"
        role="listbox"
        tabindex="-1"
        :class="$style.selectOptions"
        :aria-activedescendant="activeDescendant"
      >
        <li
          v-for="option in options"
          :id="option.value"
          :key="option.id"
          role="option"
          tabindex="0"
          @click="changeOptionHandler(option, $event)"
          @keydown="keyChangeOptionHandler(option, $event)"
        >
          {{ option.name }}
        </li>
      </ul>
    </transition>
  </div>
</template>

<script>
import { ref } from 'vue';

export default {
  props: {
    selectedOption: {
      type: String,
      required: true,
    },
    options: {
      type: Array,
      required: true,
    },
    activeDescendant: {
      type: String,
      required: true,
    },
  },
  emits: ['optionChange'],
  setup(props, { emit }) {
    const isExpanded = ref(false);

    const toggleSelectHandler = () => {
      isExpanded.value = !isExpanded.value;
    };
    const changeOptionHandler = (option) => {
      if (!(props.selectedOption === option.name)) {
        emit('optionChange', { name: option.name, value: option.value });
      }
      isExpanded.value = false;
    };
    const keyChangeOptionHandler = (option, event) => {
      if (event.keyCode === 13 || event.keyCode === 32) {
        changeOptionHandler(option);
      }
    };

    return {
      isExpanded,
      toggleSelectHandler,
      changeOptionHandler,
      keyChangeOptionHandler,
    };
  },
};
</script>

### For Animation
<style lang="scss">
.select-options {
  &-enter-from,
  &-leave-to {
    max-height: 0px;
  }
  &-enter-to,
  &-leave-from {
   max-height: 10rem;
  }
  &-enter-active,
  &-leave-active {
    transition: max-height 200ms ease-out;
    overflow: hidden;
  }
}
</style>

<style lang="scss" module>
@import '@/assets/scss/_shared';
.select {
  position: relative;
  width: 12.5rem;
  min-width: 12.5rem;
}

.selectedOption,
.selectOptions {
  background-color: $clr-elements;
  background-color: var(--clr-elements);
  font-size: .875rem;
  font-size: clamp(.75rem, 3vw, .875rem);
  width: 100%;
  box-shadow: $box-shadow-sm;
  border-radius: $border-radius-sm;
}

.selectedOption {
  border: none;
  display: flex;
  align-items: center;
  justify-content: space-between;

  padding: 1em 1.5em;

  margin-top: 2.5rem;

  .angleDownIcon {
    font-size: .875rem;
    transition: transform 200ms ease-out;
  }
  @include screenMedium {
    cursor: pointer;
    margin-top: 0;
  }

  &.active {

    .angleDownIcon {
      transform: rotate(180deg);
    }
  }
}

.selectOptions {
  list-style: none;
  margin-top: .5em;
  padding: 0 1.5em;
  position: absolute;

  > * {
    padding: .4em 0;
    @include screenMedium {
      cursor: pointer;
    }
  }

  > *:first-child {
    padding-top: 1em;
  }

  > *:last-child {
    padding-bottom: 1em;
  }
}
</style>
